import { HYPERSWITCH_SUBSCRIPTION_SERVICE } from './hyperswitchRail';
import { resolveLagoAddonCode } from './lagoAddonMap';
import { LAGO_PLAN_MAP } from './lagoPlanMap';

export const ROUND_PROVIDER = 'round';

type PaymentProvider = 'chargebee' | 'round';

type RoundGateway = {
  paypalGatewayId: string;
  businessEntityId: string;
};

/**
 * The backend names the Hyperswitch rail `round_hyperswitch`, but it is still the Round branch as
 * far as the funnel is concerned, so it normalises to one internal identity. Which SDK to mount is
 * decided from the intent the backend returns, not from this value.
 */
const PROVIDER_BY_SUBSCRIPTION_SERVICE: Record<string, PaymentProvider> = {
  chargebee: 'chargebee',
  round: ROUND_PROVIDER,
  round_hyperswitch: ROUND_PROVIDER,
};

export function resolveEffectiveProvider(requestedProvider: string | undefined): PaymentProvider | undefined {
  return requestedProvider ? PROVIDER_BY_SUBSCRIPTION_SERVICE[requestedProvider] : undefined;
}

/**
 * The two fields move together: the branch the funnel takes, and the value it echoes back to the
 * backend. Assigning either alone leaves the store describing a rail that does not exist.
 *
 * Round has one rail, Hyperswitch, so a plain `round` — from the query or from the backend — is
 * answered with it. `chargebeeOnly` pins a funnel to Chargebee whatever either of them says.
 */
export function resolveProviderAssignment(requestedProvider: string | undefined, chargebeeOnly = false):
  { provider: PaymentProvider; subscriptionService: string } | undefined {
  const requested = chargebeeOnly ? 'chargebee' : requestedProvider;
  const provider = resolveEffectiveProvider(requested);

  if (!provider || !requested) return undefined;

  return {
    provider,
    subscriptionService: provider === ROUND_PROVIDER ? HYPERSWITCH_SUBSCRIPTION_SERVICE : requested,
  };
}

// A plan with no Lago counterpart keeps its Chargebee code, so useRoundPlanFallback can downgrade it.
export function resolveEffectivePlanId(provider: string | undefined, chargebeeId: string): string {
  return provider === ROUND_PROVIDER ? (LAGO_PLAN_MAP[chargebeeId] ?? chargebeeId) : chargebeeId;
}

export function readLagoCredentials(res: any): {
  lagoPaypalClientId?: string;
  lagoHyperswitchPk?: string;
  lagoCardProvider?: string;
} {
  return {
    lagoPaypalClientId: res?.lago_paypal_client_id || undefined,
    lagoHyperswitchPk: res?.lago_hyperswitch_p_key || undefined,
    lagoCardProvider: res?.lago_card_provider || undefined,
  };
}

export function resolveGatewayName(
  provider: string,
  lagoCardProvider: string,
  chargebeeGatewayName: string,
): string {
  if (provider === ROUND_PROVIDER) return lagoCardProvider || 'stripe';

  return chargebeeGatewayName || 'stripe';
}

export function roundCardInitFields(email: string, name?: string): Record<string, string> {
  const fields: Record<string, string> = { email };
  if (name) fields.name = name;

  return fields;
}

export function roundPaypalInitPayload(
  uuid: string,
  gateway: Pick<RoundGateway, 'paypalGatewayId'>,
  currencyCode: string,
  amount?: number,
): Record<string, unknown> {
  return {
    web_user_uuid: uuid,
    paypal_gateway_id: gateway.paypalGatewayId,
    currency_code: currencyCode,
    amount,
  };
}

export function roundPaypalAcceptPayload(
  payload: Record<string, unknown>,
  uuid: string,
  gateway: RoundGateway,
): Record<string, unknown> {
  return {
    ...payload,
    web_user_uuid: uuid,
    paypal_gateway_id: gateway.paypalGatewayId,
    business_entity_id: gateway.businessEntityId,
  };
}

export function roundCardAcceptPayload(
  payload: Record<string, unknown>,
  uuid: string,
): Record<string, unknown> {
  return {
    ...payload,
    web_user_uuid: uuid,
  };
}

export function findBasePlanId(plans: SubscriptionModel[], pID: unknown): string | undefined {
  return plans.find(plan => String(plan?.id) === String(pID))?.standard?.chargebeeId;
}

export function roundAddonsPayload(
  payload: Record<string, unknown> & { item?: string },
  basePlanId?: string,
): Record<string, unknown> {
  return {
    ...payload,
    item: resolveLagoAddonCode(payload.item, basePlanId),
  };
}
